import { Component, OnInit } from '@angular/core';
import { PrepLocationsService } from 'src/app/services/prep-locations.service';
import { IPrepLocation } from 'src/app/common/models/prep-location';
import { CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import { ToastrService } from 'ngx-toastr';
import { Operation } from 'src/app/common/models/operation';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { AuthManager } from 'src/app/core/auth/auth.manager';

@Component({
  standalone: true,
  templateUrl: './walkpath-order.component.html',
  styleUrls: ['./walkpath-order.component.scss'],
  imports: [CdkDropList, CdkDrag],
})
export class WalkpathOrderComponent implements OnInit {
  public currentFacilityId: string | any;
  public facilityPrepLocations: IPrepLocation[] = [];
  public walkpathLocked = true;
  public originalWalkpathOrder: string[] = [];

  constructor(
    private _facilityManager: FacilityManager,
    private prepLocationService: PrepLocationsService,
    private toastr: ToastrService,
    private authManager: AuthManager,
  ) {}

  public ngOnInit(): void {
    this.authManager.isLoggedIn$.subscribe((isAuthed) => {
      if (isAuthed) {
        this._facilityManager.facilityIdUpdates$.subscribe((fId) => {
          this.currentFacilityId = fId;
          this.getFacilityPrepLocations();
        });
      }
    });
  }

  private getFacilityPrepLocations(): void {
    this.prepLocationService.getPrepLocations(this.currentFacilityId).subscribe({
      next: (facilityPrepLocations) => {
        this.facilityPrepLocations = facilityPrepLocations;
        this.sortByWalkpath();
        this.originalWalkpathOrder = this.facilityPrepLocations.map((prepLocation) => {
          return prepLocation.prepLocationTypeName;
        });
      },
      error: (e) => {
        this.toastr.error('Unable to load Prep Locations');
      },
    });
  }

  private sortByWalkpath(): void {
    this.facilityPrepLocations.sort((a, b) => {
      return a.walkPathOrder - b.walkPathOrder;
    });
  }

  public drop(event: CdkDragDrop<IPrepLocation[]>): void {
    moveItemInArray(this.facilityPrepLocations, event.previousIndex, event.currentIndex);
  }

  public lockButtonEvent(): void {
    this.walkpathLocked = !this.walkpathLocked;
    // if walkpath is now locked, check if anything was updated
    if (this.walkpathLocked === true) {
      const savedWalkpathOrder = this.facilityPrepLocations.map((fpl) => {
        return fpl.prepLocationTypeName;
      });

      // if walkpath was updated, attempt to save
      if (!this.isArrayEqualToOtherArray(this.originalWalkpathOrder, savedWalkpathOrder)) {
        this.saveWalkPathOrder();
      }
    }
  }

  private saveWalkPathOrder(): void {
    const saveObservables: Observable<any>[] = [];

    this.facilityPrepLocations.forEach((prepLocation: IPrepLocation, index) => {
      prepLocation.walkPathOrder = index;

      const patch: Operation[] = [
        {
          op: 'add',
          path: '/walkPathOrder',
          value: prepLocation.walkPathOrder,
        },
      ];

      saveObservables.push(
        this.prepLocationService
          .updatePrepLocation(prepLocation.facilityId, prepLocation.prepLocationTypeName, patch)
          .pipe(
            map((success) => ({ isError: false, error: null, facilityId: prepLocation.facilityId })),
            catchError((err) => of({ isError: true, error: err, facilityId: prepLocation.facilityId })),
          ),
      );
    });

    forkJoin(saveObservables).subscribe({
      next: (f) => {
        const success = this.saveToastrLogic(f);
        if (success) {
          this.originalWalkpathOrder = this.facilityPrepLocations.map((fpl) => {
            return fpl.prepLocationTypeName;
          });
        }
      },
      error: (e) => {
        this.toastr.error('Error while saving Walkpath Order: ' + e);
        return;
      },
    });
  }

  private saveToastrLogic(observableResponses: any[]): boolean {
    const successfulObservables = observableResponses.filter((e) => e.isError === false);
    const erroredObservables = observableResponses.filter((e) => e.isError === true);

    if (erroredObservables.length === 0) {
      this.toastr.success('Walkpath order successfully updated!');
    }
    if (erroredObservables.length > 0 && successfulObservables.length > 0) {
      this.toastr.warning(
        successfulObservables.length
          + ' facility prep locations were updated, however '
          + erroredObservables.length
          + ' failed to update: '
          + erroredObservables.map((eo) => eo.facilityId).join(', '),
        'Not all Facility Prep Locations updated',
        { timeOut: 10000 },
      );
    }
    if (erroredObservables.length === observableResponses.length) {
      this.toastr.error('An error occured while updating walkpath order.');
      return false;
    }
    return true;
  }

  private isArrayEqualToOtherArray(array1: string[], array2: string[]): boolean {
    return (
      array1.length === array2.length
      && array1.every(function (value, index) {
        return value === array2[index];
      })
    );
  }
}
